'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter, useSearchParams } from 'next/navigation';
import { Eye, EyeOff, Loader2, ShieldCheck, Building2, User, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';


interface SignupFormData {
  firstName: string;
  lastName: string;
  workEmail: string;
  companyName: string;
  role: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

interface SignupFormProps {
  onSwitchToLogin: () => void;
}

const roleOptions = [
  { value: 'ap_clerk', label: 'AP Clerk / Bookkeeper' },
  { value: 'ap_manager', label: 'AP Manager' },
  { value: 'cfo', label: 'CFO' },
  { value: 'controller', label: 'Controller / Finance Director' },
  { value: 'owner', label: 'Business Owner' },
];

export default function SignupForm({ onSwitchToLogin }: SignupFormProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { signUp } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  // Read invite token from URL — set by team invitation emails
  const inviteToken = searchParams.get('invite_token') || '';
  const isInvited = Boolean(inviteToken);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
  } = useForm<SignupFormData>();

  const passwordValue = watch('password', '');

  const onSubmit = async (data: SignupFormData) => {
    setLoading(true);
    try {
      await signUp(data.workEmail, data.password, {
        fullName: `${data.firstName} ${data.lastName}`,
        role: data.role,
        companyName: isInvited ? '' : data.companyName,
        // Pass invite token so the auth callback can join the right company
        inviteToken: inviteToken || undefined,
      });
      toast.success('Account created! Please verify your email.', {
        description: `We sent a verification link to ${data.workEmail}.`,
      });
      // Redirect to email verification holding screen, preserving invite token
      const params = new URLSearchParams({ email: data.workEmail });
      if (inviteToken) params.set('invite_token', inviteToken);
      router.push(`/verify-email?${params.toString()}`);
    } catch (err: any) {
      setLoading(false);
      setError('workEmail', {
        type: 'manual',
        message: err?.message || 'Failed to create account. Please try again.',
      });
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground">
          {isInvited ? 'Accept your invitation' : 'Create your workspace'}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {isInvited
            ? 'Set up your account to join your team on LedgerPay' :'Set up LedgerPay for your finance team'}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        {/* Name Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="signup-first" className="block text-sm font-semibold text-foreground mb-1.5">
              First Name
            </label>
            <div className="relative">
              <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                id="signup-first"
                type="text"
                autoComplete="given-name"
                placeholder="Sandra"
                {...register('firstName', { required: 'Required' })}
                className={`w-full pl-9 pr-3 py-2.5 text-sm border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors duration-150 ${errors.firstName ? 'border-danger' : 'border-input'}`}
              />
            </div>
            {errors.firstName && <p className="text-xs text-danger mt-1" role="alert">{errors.firstName.message}</p>}
          </div>
          <div>
            <label htmlFor="signup-last" className="block text-sm font-semibold text-foreground mb-1.5">
              Last Name
            </label>
            <input
              id="signup-last"
              type="text"
              autoComplete="family-name"
              placeholder="Chen"
              {...register('lastName', { required: 'Required' })}
              className={`w-full px-3.5 py-2.5 text-sm border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors duration-150 ${errors.lastName ? 'border-danger' : 'border-input'}`}
            />
            {errors.lastName && <p className="text-xs text-danger mt-1" role="alert">{errors.lastName.message}</p>}
          </div>
        </div>

        {/* Work Email */}
        <div>
          <label htmlFor="signup-email" className="block text-sm font-semibold text-foreground mb-1.5">
            Work Email
          </label>
          {!isInvited && (
            <p className="text-xs text-muted-foreground mb-1.5">Must be a company domain — personal emails are not supported</p>
          )}
          <input
            id="signup-email"
            type="email"
            autoComplete="email"
            placeholder="you@company.com"
            {...register('workEmail', {
              required: 'Work email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
              validate: (v) => {
                if (isInvited) return true; // invited users may use any email
                const blocked = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];
                const domain = v.split('@')[1]?.toLowerCase();
                return !blocked.includes(domain) || 'Personal email domains are not allowed';
              },
            })}
            className={`w-full px-3.5 py-2.5 text-sm border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors duration-150 ${errors.workEmail ? 'border-danger focus:ring-danger/30' : 'border-input'}`}
          />
          {errors.workEmail && <p className="text-xs text-danger mt-1" role="alert">{errors.workEmail.message}</p>}
        </div>

        {/* Company Name — hidden for invited users (they join existing company) */}
        {!isInvited && (
          <div>
            <label htmlFor="signup-company" className="block text-sm font-semibold text-foreground mb-1.5">
              Company Name
            </label>
            <div className="relative">
              <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                id="signup-company"
                type="text"
                autoComplete="organization"
                placeholder="Meridian Holdings LLC"
                {...register('companyName', {
                  required: isInvited ? false : 'Company name is required',
                  minLength: { value: 2, message: 'Company name must be at least 2 characters' },
                })}
                className={`w-full pl-9 pr-3.5 py-2.5 text-sm border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors duration-150 ${errors.companyName ? 'border-danger focus:ring-danger/30' : 'border-input'}`}
              />
            </div>
            {errors.companyName && <p className="text-xs text-danger mt-1" role="alert">{errors.companyName.message}</p>}
          </div>
        )}

        {/* Role */}
        <div>
          <label htmlFor="signup-role" className="block text-sm font-semibold text-foreground mb-1.5">
            Your Role
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">Determines your dashboard view and permission level</p>
          <div className="relative">
            <select
              id="signup-role"
              {...register('role', { required: 'Please select your role' })}
              className={`w-full appearance-none px-3.5 py-2.5 text-sm border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors duration-150 ${errors.role ? 'border-danger focus:ring-danger/30' : 'border-input'}`}
            >
              <option value="">Select your role...</option>
              {roleOptions.map(opt => (
                <option key={`role-opt-${opt.value}`} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
          {errors.role && <p className="text-xs text-danger mt-1" role="alert">{errors.role.message}</p>}
        </div>

        {/* Password */}
        <div>
          <label htmlFor="signup-password" className="block text-sm font-semibold text-foreground mb-1.5">
            Password
          </label>
          <p className="text-xs text-muted-foreground mb-1.5">Minimum 10 characters with at least one number and symbol</p>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="••••••••••••"
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 10, message: 'Password must be at least 10 characters' },
                pattern: {
                  value: /^(?=.*[0-9])(?=.*[!@#$%^&*])/,
                  message: 'Password must include a number and a special character',
                },
              })}
              className={`w-full pr-10 px-3.5 py-2.5 text-sm border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors duration-150 ${errors.password ? 'border-danger focus:ring-danger/30' : 'border-input'}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && <p className="text-xs text-danger mt-1" role="alert">{errors.password.message}</p>}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="signup-confirm" className="block text-sm font-semibold text-foreground mb-1.5">
            Confirm Password
          </label>
          <div className="relative">
            <input
              id="signup-confirm"
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="••••••••••••"
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (v) => v === passwordValue || 'Passwords do not match',
              })}
              className={`w-full pr-10 px-3.5 py-2.5 text-sm border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-colors duration-150 ${errors.confirmPassword ? 'border-danger focus:ring-danger/30' : 'border-input'}`}
            />
            <button
              type="button"
              onClick={() => setShowConfirm(v => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.confirmPassword && <p className="text-xs text-danger mt-1" role="alert">{errors.confirmPassword.message}</p>}
        </div>

        {/* Terms */}
        <div>
          <label className="flex items-start gap-2.5 cursor-pointer group">
            <input
              type="checkbox"
              {...register('agreeTerms', { required: 'You must accept the terms to continue' })}
              className="mt-0.5 w-4 h-4 rounded border-input accent-primary flex-shrink-0"
            />
            <span className="text-xs text-muted-foreground leading-snug group-hover:text-foreground transition-colors duration-150">
              I agree to LedgerPay&apos;s{' '}
              <a href="/terms" className="text-primary hover:underline font-medium">Terms of Service</a>
              {' '}and{' '}
              <a href="/privacy" className="text-primary hover:underline font-medium">Privacy Policy</a>.
              I understand that all payment authorizations are cryptographically logged and auditable.
            </span>
          </label>
          {errors.agreeTerms && <p className="text-xs text-danger mt-1" role="alert">{errors.agreeTerms.message}</p>}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-bold bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 active:scale-[0.98] transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed disabled:scale-100"
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              {isInvited ? 'Joining team...' : 'Creating workspace...'}
            </>
          ) : (
            <>
              <ShieldCheck size={16} />
              {isInvited ? 'Accept & Join Team' : 'Create Secure Workspace'}
            </>
          )}
        </button>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-5">
        Already have an account?{' '}
        <button
          onClick={onSwitchToLogin}
          className="text-primary font-semibold hover:underline transition-colors duration-150"
        >
          Sign in
        </button>
      </p>
    </div>
  );
}